import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { Application, Container, Graphics, Text, Texture } from 'pixi.js';
import { Viewport } from 'pixi-viewport';

import { LIMIT_SEATS } from './constants/constants';
import {
  ACTIVE_SECTOR_STROKE,
  DESKTOP_STAGE_MAX_HEIGHT,
  DESKTOP_ZOOM_CONTROLS_WIDTH,
  LODGE_AVAILABLE_STROKE_WIDTH,
  LODGE_DEFAULT_FILL,
  LODGE_HOVER_STROKE,
  LODGE_PRICE_HIGHLIGHT_COLOR,
  LODGE_PRICE_HIGHLIGHT_FILL_OPACITY,
  MOBILE_STAGE_HEIGHT,
  MOBILE_NEAREST_SEAT_RADIUS,
  PIXI_LODGE_STROKE_SCALE,
  PIXI_SECTOR_STROKE_SCALE,
  VIP_FAN_SECTOR_REGEX,
} from './constants/seatMap';
import type { SeatMapProps } from './models/types';
import { normalizeSectorName } from './utils/normalizeSectorName';
import { extractLodgeLabels, type SectorLabelData } from './utils/svgParser';
import SectorOverlay from './components/SectorOverlay';
import SeatTooltip from './components/SeatTooltip';
import { SoldOutBanner } from './components/SoldOutBanner';
import ZoomControls from './components/ZoomControls';
import type {
  BackgroundAsset,
  Bounds2D,
  ParsedSeatData,
  Point2D,
  SceneRuntime,
  SeatHoverPayload,
  SeatTapIndex,
  SeatRenderContext,
  SeatRenderEntry,
  ViewportCameraBounds,
} from './models/types';
import {
  buildCenteredViewportPosition,
  buildViewportPositionForScreenAnchor,
  clampViewportDragPosition,
  clampViewportPinchPosition,
  clampViewportPosition,
  setViewportCamera,
} from './utils/camera';
import {
  clampViewportDragPosition as clampLegacyViewportDragPosition,
  clampViewportPinchPosition as clampLegacyViewportPinchPosition,
  clampViewportPosition as clampLegacyViewportPosition,
} from './utils/cameraLegacy';
import {
  buildLodgePriceGlowGraphic,
  buildSelectedSeatPayload,
  drawSeatGraphic,
} from './utils/render';
import { buildScene } from './utils/sceneBuilder';
import { buildSeatInfoIndex, parseExternalSeats, parseSeatContainerId } from './utils/seatData';
import { buildLodgeMarkerSectorNames, createSeatMapSectorNormalizer } from './utils/sectorIdentity';
import { buildSeatTapIndex, findNearestSeatEntry } from './utils/seatTap';
import {
  fallbackRules,
  getSectorPreviewTransform,
  parseSeatMapRules,
  resolveRuleSet,
} from './utils/seatMapRules';
import { extractPreviewSectors } from './utils/sectorPreview';
import {
  buildLodgeSelectionPayloadBySectorName,
  buildLodgeSectorsWithSeats,
  buildPriceHighlightedLodgeSectorNames,
  buildSelectedLodgeSectorNames,
  buildVisibleSectorNames,
  hasClosedSectorArea,
  isRoyalVipFamilySector,
} from './utils/sectorHelpers';
import { buildSectorCenters } from './utils/sectorGeometry';
import { isMegasportVenue } from './utils/isMegasportVenue';
import { isSeatMapClockwiseRotationShow } from './utils/seatMapViewport';

const isLodgeSector = (sectorName: string | null | undefined) =>
  !!sectorName && normalizeSectorName(sectorName).toLowerCase().includes('лож');

const PINCH_TAP_SUPPRESSION_MS = 400;
// Временный mobile-only background asset для Мегаспорта.
// Техдолг: такой global hardcode не должен оставаться
// внутри универсального компонента карты.
const MOBILE_BACKGROUND_TEXTURE_PATH = '/seatmap/schema-mobile-bg.png';

interface RenderedSeatMapState {
  readonly eventId: number;
  readonly scene: SceneRuntime;
  readonly parsedSeats: readonly ParsedSeatData[];
  readonly backgroundAsset: BackgroundAsset | null;
  readonly schemaSvg: string | null | undefined;
  readonly availableSeatsCount: number;
  readonly checkSeatId?: number;
  readonly checkSeatX?: number;
  readonly checkSeatY?: number;
}

const parseSvgLogicalSize = (svgText: string | null | undefined) => {
  if (!svgText || !svgText.trim().startsWith('<')) return null;

  const viewBoxMatch = svgText.match(
    /viewBox=["']\s*[\d.\-]+\s+[\d.\-]+\s+([\d.]+)\s+([\d.]+)\s*["']/i
  );
  if (viewBoxMatch) {
    const width = Number.parseFloat(viewBoxMatch[1]);
    const height = Number.parseFloat(viewBoxMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
  }

  const widthMatch = svgText.match(/width=["']([\d.]+)/i);
  const heightMatch = svgText.match(/height=["']([\d.]+)/i);
  if (widthMatch && heightMatch) {
    const width = Number.parseFloat(widthMatch[1]);
    const height = Number.parseFloat(heightMatch[1]);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
  }

  return null;
};

const measureSvgVisibleBounds = (
  svgText: string | null | undefined,
  logicalSize: { width: number; height: number } | null
): Bounds2D | undefined => {
  if (!svgText || !logicalSize || !svgText.trim().startsWith('<')) return undefined;

  const documentNode = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const parsedSvg = documentNode.documentElement;
  if (!(parsedSvg instanceof SVGSVGElement)) return undefined;

  documentNode.querySelectorAll('script, foreignObject').forEach(node => node.remove());
  // Hall Editor exports include an opaque page-sized frame that is not map content.
  // Keep it in the rendered SVG, but exclude it from camera fitting.
  documentNode.querySelector('#hall-editor-export-frame')?.remove();
  documentNode.querySelectorAll('*').forEach(node => {
    for (const attribute of Array.from(node.attributes)) {
      if (attribute.name.toLowerCase().startsWith('on')) {
        node.removeAttribute(attribute.name);
      }
    }
  });

  const measurementHost = document.createElement('div');
  measurementHost.style.cssText =
    'position:fixed;left:-100000px;top:-100000px;width:1px;height:1px;overflow:hidden;pointer-events:none;';
  const svg = document.importNode(parsedSvg, true);
  measurementHost.appendChild(svg);
  document.body.appendChild(measurementHost);

  try {
    const contentBounds = svg.getBBox();
    const viewBox = svg.viewBox.baseVal;
    const viewBoxWidth = viewBox.width || logicalSize.width;
    const viewBoxHeight = viewBox.height || logicalSize.height;
    const minX = Math.max(contentBounds.x, viewBox.x);
    const minY = Math.max(contentBounds.y, viewBox.y);
    const maxX = Math.min(contentBounds.x + contentBounds.width, viewBox.x + viewBoxWidth);
    const maxY = Math.min(contentBounds.y + contentBounds.height, viewBox.y + viewBoxHeight);

    if (maxX <= minX || maxY <= minY) return undefined;

    const scaleX = logicalSize.width / viewBoxWidth;
    const scaleY = logicalSize.height / viewBoxHeight;

    return {
      minX: (minX - viewBox.x) * scaleX,
      minY: (minY - viewBox.y) * scaleY,
      maxX: (maxX - viewBox.x) * scaleX,
      maxY: (maxY - viewBox.y) * scaleY,
    };
  } catch {
    return undefined;
  } finally {
    measurementHost.remove();
  }
};

const SeatMapWebgl: FC<SeatMapProps> = ({
  onSeatSelect,
  selectedSeats,
  seatsInfoData = [],
  canvasWidth,
  canvasHeight,
  externalSeatsData,
  activePrices = [],
  eventId,
  isSoldOut,
  isTimeoutModalOpen,
  schemaSvg,
  seatMapRules,
  showId,
  uniquePrices,
  hallName,
  address,
}) => {
  const [seatTooltip, setSeatTooltip] = useState<{
    x: number;
    y: number;
    price: number;
    seatInfo: string;
    sectorName: string;
    visibility?: string | null;
  } | null>(null);
  const [hoveredSectorName, setHoveredSectorName] = useState<string | null>(null);
  const [sectorSvgText, setSectorSvgText] = useState<string | null>(null);
  const [cameraSnapshot, setCameraSnapshot] = useState({
    x: 0,
    y: 0,
    scale: 1,
  });
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasMountRef = useRef<HTMLDivElement | null>(null);
  const [scene, setScene] = useState<SceneRuntime | null>(null);
  const renderEntriesRef = useRef<SeatRenderEntry[]>([]);
  const renderEntryByRenderIdRef = useRef<Map<string, SeatRenderEntry>>(new Map());
  const renderEntryBySeatIdRef = useRef<Map<number, SeatRenderEntry[]>>(new Map());
  const renderEntryByPriceRef = useRef<Map<number, SeatRenderEntry[]>>(new Map());
  const seatTapIndexRef = useRef<SeatTapIndex | null>(null);
  const prevSelectedSeatIdsRef = useRef<Set<number>>(new Set());
  const prevActivePriceSetRef = useRef<Set<number>>(new Set());
  const activePriceSetRef = useRef<Set<number>>(new Set(activePrices));
  const visualSelectedSeatIdsRef = useRef<Set<number>>(new Set());
  const hoveredSeatRenderIdRef = useRef<string | null>(null);
  const minScaleRef = useRef(0.08);
  const buildMetricsRef = useRef<{
    normalizedOffset: Point2D;
    rotationPivot: Point2D;
    cameraBounds: ViewportCameraBounds;
  } | null>(null);
  const sectorHitPathRefs = useRef<Map<string, SVGPathElement>>(new Map());
  const seatHoverActiveRef = useRef(false);
  const lastSeatTapTsRef = useRef(0);
  const pendingSeatSelectionRef = useRef<{
    pointerId: number;
    seat: SeatMapProps['selectedSeats'][number];
  } | null>(null);
  const onSeatSelectRef = useRef(onSeatSelect);
  const sectorZoomLevelsRef = useRef<Map<string, number>>(new Map());
  const isDraggingRef = useRef(false);
  const lastDragEndTsRef = useRef(0);
  const lastPinchEndTsRef = useRef(0);
  const activePointersRef = useRef<Map<number, Point2D>>(new Map());
  const dragStateRef = useRef<{
    pointerId: number;
    startPointer: Point2D;
    startPosition: Point2D;
  } | null>(null);
  const pinchStateRef = useRef<{
    startDistance: number;
    startScale: number;
    anchorWorldPoint: Point2D;
    needsAnchorReset: boolean;
  } | null>(null);
  const [backgroundAsset, setBackgroundAsset] = useState<BackgroundAsset | null>(null);

  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  // Временный guard: только Мегаспорт может использовать mobile PNG background.
  // Все остальные площадки должны fallback'иться на schemaSvg,
  // чтобы не подмешивать чужую подложку в другую арену.
  const shouldUseMegasportMobileBackground = useMemo(
    () => isMegasportVenue({ hallName, address }),
    [address, hallName]
  );
  const ruleSource = useMemo(
    () => parseSeatMapRules(seatMapRules) ?? fallbackRules,
    [seatMapRules]
  );
  const rules = useMemo(
    () => resolveRuleSet(ruleSource, showId ?? eventId),
    [eventId, ruleSource, showId]
  );
  const usesAdjustedViewport = isSeatMapClockwiseRotationShow(showId);
  const sectorPreviewTransform = useMemo(() => getSectorPreviewTransform(rules), [rules]);
  const stageWidth = Math.min(canvasWidth, 1200);
  const stageHeight = Math.min(
    canvasHeight,
    isMobile ? MOBILE_STAGE_HEIGHT : DESKTOP_STAGE_MAX_HEIGHT
  );
  const [hostViewportSize, setHostViewportSize] = useState(() => ({
    width: stageWidth,
    height: stageHeight,
  }));
  const viewportWidth = hostViewportSize.width;
  const viewportHeight = hostViewportSize.height;

  const sectorPaths = useMemo(() => extractPreviewSectors(sectorSvgText), [sectorSvgText]);
  const normalizeMapSectorName = useMemo(
    () =>
      createSeatMapSectorNormalizer(
        seatsInfoData.map(seat => seat.sector),
        sectorPaths.map(sector => sector.name),
        externalSeatsData.map(seat => parseSeatContainerId(seat.id).sectorName)
      ),
    [externalSeatsData, seatsInfoData, sectorPaths]
  );
  const seatInfoIndex = useMemo(
    () => buildSeatInfoIndex(seatsInfoData, normalizeMapSectorName),
    [normalizeMapSectorName, seatsInfoData]
  );

  const parsedSeats = useMemo(
    () => parseExternalSeats(externalSeatsData, seatInfoIndex, normalizeMapSectorName),
    [externalSeatsData, normalizeMapSectorName, seatInfoIndex]
  );
  const { lodgeMarkerSectorNames, outlineSectorPaths } = useMemo(() => {
    const markerSectorNames = buildLodgeMarkerSectorNames({
      parsedSeats,
      sectorNames: sectorPaths.map(sector => sector.name),
      normalizeSectorName: normalizeMapSectorName,
    });

    return {
      lodgeMarkerSectorNames: markerSectorNames,
      outlineSectorPaths: sectorPaths.filter(
        sector => !markerSectorNames.has(normalizeMapSectorName(sector.name))
      ),
    };
  }, [parsedSeats, sectorPaths, normalizeMapSectorName]);
  const [renderedSeatMap, setRenderedSeatMap] = useState<RenderedSeatMapState | null>(null);
  const isSeatMapReady =
    renderedSeatMap?.eventId === eventId &&
    renderedSeatMap.scene === scene &&
    renderedSeatMap.parsedSeats === parsedSeats &&
    renderedSeatMap.backgroundAsset === backgroundAsset &&
    renderedSeatMap.schemaSvg === schemaSvg;
  const lodgeLabels = useMemo(() => extractLodgeLabels(sectorSvgText ?? ''), [sectorSvgText]);
  const sectorPathByName = useMemo(
    () => new Map(outlineSectorPaths.map(sector => [sector.name, sector])),
    [outlineSectorPaths]
  );
  const lodgeLabelMap = useMemo(() => {
    const map = new Map<string, SectorLabelData>();
    lodgeLabels.forEach(label => map.set(normalizeMapSectorName(label.name), label));
    return map;
  }, [lodgeLabels, normalizeMapSectorName]);
  const sectorCenters = useMemo(() => buildSectorCenters(sectorPaths), [sectorPaths]);
  const normalizedSectorPathMap = useMemo(() => {
    const map = new Map<string, string>();
    sectorPaths.forEach(sector => {
      const normalizedName = normalizeMapSectorName(sector.name);
      const existing = map.get(normalizedName);
      if (!existing) {
        map.set(normalizedName, sector.name);
        return;
      }

      const existingIsVipFan = /vip\s*fan/i.test(existing);
      const currentIsVipFan = VIP_FAN_SECTOR_REGEX.test(sector.name);
      if (existingIsVipFan && !currentIsVipFan) {
        map.set(normalizedName, sector.name);
      }
    });
    return map;
  }, [normalizeMapSectorName, sectorPaths]);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const updateViewportSize = () => {
      const rect = host.getBoundingClientRect();
      const nextSize = {
        width: Math.max(1, Math.round(rect.width || stageWidth)),
        height: Math.max(1, Math.round(rect.height || stageHeight)),
      };
      setHostViewportSize(nextSize);
    };

    updateViewportSize();

    const observer = new ResizeObserver(() => {
      updateViewportSize();
    });
    observer.observe(host);

    return () => {
      observer.disconnect();
    };
  }, [stageHeight, stageWidth]);
  const seatIdToSectorName = useMemo(() => {
    const map = new Map<number, string>();
    seatsInfoData.forEach(seatInfo => {
      map.set(seatInfo.id, seatInfo.sector);
    });
    return map;
  }, [seatsInfoData]);
  const lodgeSectorsWithSeats = useMemo(
    () => buildLodgeSectorsWithSeats(seatsInfoData, normalizeMapSectorName, isLodgeSector),
    [isLodgeSector, normalizeMapSectorName, seatsInfoData]
  );
  const selectedLodgeSectorNames = useMemo(
    () =>
      buildSelectedLodgeSectorNames({
        selectedSeats,
        seatIdToSectorName,
        isLodgeSector,
        normalizedSectorPathMap,
        normalizeSectorName: normalizeMapSectorName,
      }),
    [
      isLodgeSector,
      normalizeMapSectorName,
      normalizedSectorPathMap,
      seatIdToSectorName,
      selectedSeats,
    ]
  );
  const lodgeSelectionPayloadBySectorName = useMemo(
    () =>
      buildLodgeSelectionPayloadBySectorName({
        seatsInfoData,
        isLodgeSector,
        normalizedSectorPathMap,
        normalizeSectorName: normalizeMapSectorName,
        eventId,
      }),
    [eventId, isLodgeSector, normalizeMapSectorName, normalizedSectorPathMap, seatsInfoData]
  );
  // Стабильная ссылка не даёт пересобирать Pixi-слой при движении камеры.
  const priceHighlightedLodgeSectorNames = useMemo(
    () =>
      buildPriceHighlightedLodgeSectorNames({
        seatsInfoData,
        activePrices,
        normalizeSectorName: normalizeMapSectorName,
        normalizedSectorPathMap,
      }),
    [activePrices, normalizeMapSectorName, normalizedSectorPathMap, seatsInfoData]
  );

  const applyCamera = (
    currentScene: SceneRuntime,
    position: Point2D,
    scale: number,
    _source: string
  ) => {
    setViewportCamera(currentScene.viewport, position, scale);
    setCameraSnapshot({
      x: position.x,
      y: position.y,
      scale,
    });
  };

  const redrawSeatSelectionState = (seatId: number, shouldBeSelected: boolean) => {
    const selectedSeatIds = new Set(visualSelectedSeatIdsRef.current);
    if (shouldBeSelected) {
      selectedSeatIds.add(seatId);
    } else {
      selectedSeatIds.delete(seatId);
    }
    visualSelectedSeatIdsRef.current = selectedSeatIds;
    const activePriceSet = activePriceSetRef.current;
    const hasActivePrices = activePriceSet.size > 0;
    const renderContext: SeatRenderContext = {
      selectedSeatIds,
      activePriceSet,
      hasActivePrices,
      hoveredRenderId: hoveredSeatRenderIdRef.current,
    };

    const entries = renderEntryBySeatIdRef.current.get(seatId);
    entries?.forEach(entry => drawSeatGraphic(entry.graphic, entry, renderContext));
  };

  const buildSeatSelectionFromEntry = (entry: SeatRenderEntry) => {
    if (!entry.seatInfo) return null;

    return buildSelectedSeatPayload(entry.seat, entry.seatInfo, eventId);
  };

  useEffect(() => {
    onSeatSelectRef.current = onSeatSelect;
  }, [onSeatSelect]);

  useEffect(() => {
    if (!schemaSvg) {
      setSectorSvgText(null);
      return;
    }

    const isInlineSvg = typeof schemaSvg === 'string' && schemaSvg.trim().startsWith('<');
    if (isInlineSvg) {
      setSectorSvgText(schemaSvg);
      return;
    }

    let isDisposed = false;

    const loadSvgText = async () => {
      try {
        const response = await fetch(schemaSvg);
        const text = await response.text();
        if (!isDisposed) {
          setSectorSvgText(text);
        }
      } catch {
        if (!isDisposed) {
          setSectorSvgText(null);
        }
      }
    };

    loadSvgText();

    return () => {
      isDisposed = true;
    };
  }, [schemaSvg]);

  useEffect(() => {
    if (!schemaSvg) {
      setBackgroundAsset(current => {
        current?.texture.destroy(true);
        return null;
      });
      return;
    }

    let isDisposed = false;
    const logicalSize = parseSvgLogicalSize(schemaSvg);
    const visibleBounds = measureSvgVisibleBounds(schemaSvg, logicalSize);
    const svgSource =
      typeof schemaSvg === 'string' && schemaSvg.trim().startsWith('<')
        ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(schemaSvg)}`
        : schemaSvg;
    // Текущее временное поведение:
    // - mobile + Мегаспорт -> сначала PNG, потом fallback на schemaSvg
    // - все остальные случаи -> сразу schemaSvg
    const sources =
      isMobile && shouldUseMegasportMobileBackground
        ? [
            { url: MOBILE_BACKGROUND_TEXTURE_PATH, alpha: 1 },
            { url: svgSource, alpha: 0.7 },
          ]
        : [{ url: svgSource, alpha: 0.7 }];

    const loadSource = (index: number) => {
      if (isDisposed) return;

      const candidate = sources[index];
      if (!candidate?.url) {
        setBackgroundAsset(current => {
          current?.texture.destroy(true);
          return null;
        });
        return;
      }

      const image = new window.Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (isDisposed) return;
        setBackgroundAsset(current => {
          current?.texture.destroy(true);
          return {
            texture: Texture.from(image),
            width: logicalSize?.width ?? image.width,
            height: logicalSize?.height ?? image.height,
            visibleBounds: candidate.url === svgSource ? visibleBounds : undefined,
            alpha: candidate.alpha,
          };
        });
      };
      image.onerror = () => {
        if (isDisposed) return;
        loadSource(index + 1);
      };
      image.src = candidate.url;
    };

    loadSource(0);

    return () => {
      isDisposed = true;
    };
  }, [isMobile, schemaSvg, shouldUseMegasportMobileBackground]);

  useEffect(() => {
    let isDisposed = false;
    let createdScene: SceneRuntime | null = null;

    const init = async () => {
      if (!canvasMountRef.current) return;

      const app = new Application();
      await app.init({
        width: viewportWidth,
        height: viewportHeight,
        backgroundAlpha: 0,
        antialias: true,
        autoDensity: true,
        resolution: Math.max(1, window.devicePixelRatio || 1),
      });

      if (isDisposed) {
        app.destroy(true);
        return;
      }

      canvasMountRef.current.appendChild(app.canvas);

      const viewport = new Viewport({
        screenWidth: viewportWidth,
        screenHeight: viewportHeight,
        worldWidth: Math.max(1, stageWidth),
        worldHeight: Math.max(1, stageHeight),
        events: app.renderer.events,
      });

      const contentRoot = new Container();
      const backgroundLayer = new Container();
      const seatLayer = new Container();
      const sectorLayer = new Container();
      contentRoot.addChild(backgroundLayer);
      contentRoot.addChild(seatLayer);
      contentRoot.addChild(sectorLayer);
      viewport.addChild(contentRoot);
      app.stage.addChild(viewport);

      createdScene = {
        app,
        viewport,
        contentRoot,
        backgroundLayer,
        seatLayer,
        sectorLayer,
      };
      setScene(createdScene);
    };

    init();

    return () => {
      isDisposed = true;
      setSeatTooltip(null);
      renderEntriesRef.current = [];
      renderEntryBySeatIdRef.current.clear();
      renderEntryByPriceRef.current.clear();
      seatTapIndexRef.current = null;
      prevSelectedSeatIdsRef.current = new Set();
      prevActivePriceSetRef.current = new Set();
      if (createdScene) {
        createdScene.viewport.destroy({ children: true });
        createdScene.app.destroy(true);
      }
      setScene(currentScene => (currentScene === createdScene ? null : currentScene));
      if (canvasMountRef.current) canvasMountRef.current.innerHTML = '';
    };
  }, [stageHeight, stageWidth, viewportHeight, viewportWidth]);

  useEffect(() => {
    if (!scene) return;
    if (
      scene.app.destroyed ||
      scene.viewport.destroyed ||
      scene.contentRoot.destroyed ||
      scene.backgroundLayer.destroyed ||
      scene.seatLayer.destroyed
    ) {
      return;
    }
    // Не строим промежуточный кадр без backgroundAsset:
    // иначе на mobile сначала появляется карта без фона,
    // а после асинхронной загрузки подложки сцена пересобирается со скачком.
    if (schemaSvg && !backgroundAsset) {
      return;
    }
    const handleSeatPointerEnter = ({ event, entry }: SeatHoverPayload) => {
      if (isMobile) return;
      const host = hostRef.current;
      if (!host || !entry.seatInfo) return;

      seatHoverActiveRef.current = true;
      const resolvedSectorName =
        normalizedSectorPathMap.get(
          normalizeMapSectorName(entry.seatInfo.sectorName ?? entry.seat.sectorName)
        ) ??
        entry.seatInfo.sectorName ??
        entry.seat.sectorName;
      setHoveredSectorName(resolvedSectorName ?? null);
      host.style.cursor = 'pointer';
      const previousHoveredRenderId = hoveredSeatRenderIdRef.current;
      hoveredSeatRenderIdRef.current = entry.renderId;
      const activePriceSet = activePriceSetRef.current;
      const renderContext: SeatRenderContext = {
        selectedSeatIds: new Set(visualSelectedSeatIdsRef.current),
        activePriceSet,
        hasActivePrices: activePriceSet.size > 0,
        hoveredRenderId: entry.renderId,
      };
      drawSeatGraphic(entry.graphic, entry, renderContext);
      if (previousHoveredRenderId && previousHoveredRenderId !== entry.renderId) {
        const previousEntry = renderEntryByRenderIdRef.current.get(previousHoveredRenderId);
        if (previousEntry) {
          drawSeatGraphic(previousEntry.graphic, previousEntry, renderContext);
        }
      }
      setSeatTooltip({
        x: event.global.x,
        y: event.global.y - 30,
        price: entry.seatInfo.price,
        seatInfo:
          entry.seat.row || entry.seat.seat
            ? `Место ${entry.seat.seat}, Ряд ${entry.seat.row}`
            : '',
        sectorName: entry.seatInfo.sectorName ?? entry.seat.sectorName,
        visibility: entry.seatInfo.visibility,
      });
    };

    const handleSeatPointerMove = ({ event }: SeatHoverPayload) => {
      if (isMobile) return;
      setSeatTooltip(current =>
        current
          ? {
              ...current,
              x: event.global.x,
              y: event.global.y - 30,
            }
          : current
      );
    };

    const handleSeatPointerOut = () => {
      if (isMobile) return;
      const host = hostRef.current;
      const hoveredRenderId = hoveredSeatRenderIdRef.current;
      seatHoverActiveRef.current = false;
      hoveredSeatRenderIdRef.current = null;
      setSeatTooltip(null);
      if (host) host.style.cursor = 'default';
      if (hoveredRenderId) {
        const entry = renderEntryByRenderIdRef.current.get(hoveredRenderId);
        if (entry) {
          const activePriceSet = activePriceSetRef.current;
          drawSeatGraphic(entry.graphic, entry, {
            selectedSeatIds: new Set(visualSelectedSeatIdsRef.current),
            activePriceSet,
            hasActivePrices: activePriceSet.size > 0,
            hoveredRenderId: null,
          });
        }
      }
    };

    const builtScene = buildScene({
      scene,
      parsedSeats,
      lodgeMarkerSectorNames,
      seatInfoIndex,
      normalizeSectorName: normalizeMapSectorName,
      backgroundAsset,
      selectedSeats,
      activePrices,
      uniquePrices,
      canvasWidth: viewportWidth,
      canvasHeight: viewportHeight,
      rules,
      showId,
      isMobile,
      sectorPreviewTransform,
      eventId,
      onSeatPointerDown: (pointerId, seat) => {
        pendingSeatSelectionRef.current = {
          pointerId,
          seat,
        };
      },
      onSeatSelect: seat => {
        onSeatSelectRef.current(seat);
      },
      onSeatPointerEnter: handleSeatPointerEnter,
      onSeatPointerMove: handleSeatPointerMove,
      onSeatPointerOut: handleSeatPointerOut,
    });

    renderEntriesRef.current = builtScene.renderEntries;
    renderEntryByRenderIdRef.current = new Map(
      builtScene.renderEntries.map(entry => [entry.renderId, entry])
    );
    renderEntryBySeatIdRef.current = builtScene.renderEntryBySeatId;
    renderEntryByPriceRef.current = builtScene.renderEntryByPrice;
    seatTapIndexRef.current = buildSeatTapIndex(builtScene.renderEntries);
    minScaleRef.current = builtScene.minimumScale;
    buildMetricsRef.current = {
      normalizedOffset: builtScene.normalizedOffset,
      rotationPivot: builtScene.rotationPivot,
      cameraBounds: builtScene.cameraBounds,
    };
    setCameraSnapshot({
      x: scene.viewport.x,
      y: scene.viewport.y,
      scale: scene.viewport.scale.x,
    });

    const checkSeatEntry = builtScene.renderEntries.find(
      entry => entry.seat.isAvailable && entry.seatInfo
    );
    const checkSeatPoint = checkSeatEntry
      ? scene.seatLayer.toGlobal({
          x: checkSeatEntry.seat.x,
          y: checkSeatEntry.seat.y,
        })
      : null;

    setRenderedSeatMap({
      eventId,
      scene,
      parsedSeats,
      backgroundAsset,
      schemaSvg,
      availableSeatsCount: builtScene.renderEntryBySeatId.size,
      checkSeatId: checkSeatEntry?.seatInfo?.id,
      checkSeatX: checkSeatPoint ? Math.round(checkSeatPoint.x * 100) / 100 : undefined,
      checkSeatY: checkSeatPoint ? Math.round(checkSeatPoint.y * 100) / 100 : undefined,
    });
  }, [
    backgroundAsset,
    viewportHeight,
    viewportWidth,
    eventId,
    parsedSeats,
    lodgeMarkerSectorNames,
    rules,
    showId,
    usesAdjustedViewport,
    scene,
    seatInfoIndex,
    normalizeMapSectorName,
    normalizedSectorPathMap,
    schemaSvg,
    sectorPreviewTransform,
    uniquePrices,
  ]);

  useEffect(() => {
    visualSelectedSeatIdsRef.current = new Set(selectedSeats.map(seat => seat.seatId));
  }, [selectedSeats]);

  useEffect(() => {
    sectorHitPathRefs.current.clear();
  }, [backgroundAsset, parsedSeats, scene, sectorPaths, sectorPreviewTransform]);

  useEffect(() => {
    const selectedSeatIds = new Set(selectedSeats.map(seat => seat.seatId));
    visualSelectedSeatIdsRef.current = new Set(selectedSeatIds);
    const activePriceSet = new Set(activePrices);
    // Pixi-обработчики живут дольше рендера и должны читать текущий фильтр.
    activePriceSetRef.current = activePriceSet;
    const hasActivePrices = activePriceSet.size > 0;
    const prevSelectedSeatIds = prevSelectedSeatIdsRef.current;
    const prevActivePriceSet = prevActivePriceSetRef.current;
    const filterChanged =
      prevActivePriceSet.size !== activePriceSet.size ||
      [...activePriceSet].some(price => !prevActivePriceSet.has(price));

    const renderContext: SeatRenderContext = {
      selectedSeatIds,
      activePriceSet,
      hasActivePrices,
      hoveredRenderId: hoveredSeatRenderIdRef.current,
    };

    if (filterChanged) {
      const hadActivePrices = prevActivePriceSet.size > 0;
      const hasGlobalFilterModeChange = hadActivePrices !== hasActivePrices;

      if (hasGlobalFilterModeChange) {
        // Переход между режимами "без фильтра" и "есть фильтр" меняет состояние
        // всех мест с ценой, поэтому здесь неизбежен полный проход по индексированным группам.
        renderEntryByPriceRef.current.forEach(entries => {
          entries.forEach(entry => drawSeatGraphic(entry.graphic, entry, renderContext));
        });
      } else {
        const changedPrices = new Set<number>();
        prevActivePriceSet.forEach(price => {
          if (!activePriceSet.has(price)) changedPrices.add(price);
        });
        activePriceSet.forEach(price => {
          if (!prevActivePriceSet.has(price)) changedPrices.add(price);
        });

        changedPrices.forEach(price => {
          const entries = renderEntryByPriceRef.current.get(price);
          entries?.forEach(entry => drawSeatGraphic(entry.graphic, entry, renderContext));
        });
      }
    } else {
      const changedSeatIds = new Set<number>();
      prevSelectedSeatIds.forEach(seatId => {
        if (!selectedSeatIds.has(seatId)) changedSeatIds.add(seatId);
      });
      selectedSeatIds.forEach(seatId => {
        if (!prevSelectedSeatIds.has(seatId)) changedSeatIds.add(seatId);
      });

      changedSeatIds.forEach(seatId => {
        const entries = renderEntryBySeatIdRef.current.get(seatId);
        entries?.forEach(entry => drawSeatGraphic(entry.graphic, entry, renderContext));
      });
    }

    if (!filterChanged && prevSelectedSeatIds.size === 0 && selectedSeatIds.size === 0) {
      for (const entry of renderEntriesRef.current) {
        drawSeatGraphic(entry.graphic, entry, renderContext);
      }
    }

    prevSelectedSeatIdsRef.current = selectedSeatIds;
    prevActivePriceSetRef.current = activePriceSet;
  }, [activePrices, selectedSeats]);

  const zoomByFactor = (factor: number) => {
    const buildMetrics = buildMetricsRef.current;
    if (!scene || !buildMetrics) return;
    const { viewport } = scene;

    const oldScale = viewport.scale.x;
    const newScale = Math.max(minScaleRef.current, Math.min(2, oldScale * factor));
    if (newScale === oldScale) return;

    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;
    const anchoredWorldPoint = viewport.toWorld({
      x: centerX,
      y: centerY,
    });
    const unclampedPosition = buildViewportPositionForScreenAnchor(
      anchoredWorldPoint,
      {
        x: centerX,
        y: centerY,
      },
      newScale
    );
    const clampedPosition = (
      usesAdjustedViewport ? clampViewportPosition : clampLegacyViewportPosition
    )({
      position: unclampedPosition,
      scale: newScale,
      bounds: buildMetrics.cameraBounds.bounds,
      screenWidth: buildMetrics.cameraBounds.screenWidth,
      screenHeight: buildMetrics.cameraBounds.screenHeight,
      isMobile,
    });

    applyCamera(scene, clampedPosition, newScale, 'zoom-controls');
  };

  const canZoomIn = cameraSnapshot.scale < 2;
  const canZoomOut = cameraSnapshot.scale > minScaleRef.current;

  useEffect(() => {
    const host = hostRef.current;
    if (!scene || !host) return;

    const onWheel = (event: WheelEvent) => {
      const buildMetrics = buildMetricsRef.current;
      const isTrackpadPinch =
        event.ctrlKey && (event.deltaMode === WheelEvent.DOM_DELTA_PIXEL || event.deltaMode === 0);

      if (!isTrackpadPinch) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const { viewport } = scene;
      if (!buildMetrics) return;
      const oldScale = viewport.scale.x;
      const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
      const newScale = Math.max(minScaleRef.current, Math.min(2, oldScale * factor));

      if (newScale === oldScale) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const rect = host.getBoundingClientRect();
      const screenPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      event.preventDefault();
      event.stopPropagation();
      const worldPoint = viewport.toWorld(screenPoint);
      const unclampedPosition = buildViewportPositionForScreenAnchor(
        worldPoint,
        screenPoint,
        newScale
      );
      const clampedPosition = (
        usesAdjustedViewport ? clampViewportPosition : clampLegacyViewportPosition
      )({
        position: unclampedPosition,
        scale: newScale,
        bounds: buildMetrics.cameraBounds.bounds,
        screenWidth: buildMetrics.cameraBounds.screenWidth,
        screenHeight: buildMetrics.cameraBounds.screenHeight,
        isMobile,
      });
      applyCamera(scene, clampedPosition, newScale, 'wheel');
    };

    host.addEventListener('wheel', onWheel, { passive: false, capture: true });

    return () => {
      host.removeEventListener('wheel', onWheel, true);
    };
  }, [isMobile, scene, usesAdjustedViewport]);

  useEffect(() => {
    const host = hostRef.current;
    if (!scene || !host || !buildMetricsRef.current) return;

    if (!isMobile) {
      host.style.cursor = 'grab';
    }

    const isControlTarget = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest('[data-seatmap-control]'));

    const getHostPoint = (event: PointerEvent): Point2D => {
      const rect = host.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const distanceBetweenPoints = (first: Point2D, second: Point2D) =>
      Math.hypot(first.x - second.x, first.y - second.y);

    const midpoint = (first: Point2D, second: Point2D): Point2D => ({
      x: (first.x + second.x) / 2,
      y: (first.y + second.y) / 2,
    });

    const findNearestSeatSelection = (screenPoint: Point2D) => {
      const seatTapIndex = seatTapIndexRef.current;
      if (!seatTapIndex) return null;

      const seatLayerPoint = scene.seatLayer.toLocal(
        scene.viewport.toWorld(screenPoint),
        scene.viewport
      );
      const nearestSeatRadius = MOBILE_NEAREST_SEAT_RADIUS;
      const nearestEntry = findNearestSeatEntry({
        seatTapIndex,
        seatLayerPoint,
        nearestSeatRadius,
      });

      return nearestEntry ? buildSeatSelectionFromEntry(nearestEntry) : null;
    };

    const findSectorAtClientPoint = (clientX: number, clientY: number) => {
      const screenPoint = new DOMPoint(clientX, clientY);

      for (const sector of sectorPaths) {
        const path = sectorHitPathRefs.current.get(sector.name);
        if (!path) continue;

        const screenMatrix = path.getScreenCTM();
        if (!screenMatrix) continue;

        const localPoint = screenPoint.matrixTransform(screenMatrix.inverse());
        const hitByFill = hasClosedSectorArea(sector.path, sector.type)
          ? path.isPointInFill(localPoint)
          : false;
        const hitByStroke =
          !hitByFill && !hasClosedSectorArea(sector.path, sector.type)
            ? path.isPointInStroke(localPoint)
            : false;

        if (hitByFill || hitByStroke) {
          return sector;
        }
      }

      return null;
    };

    const applyDraggedPosition = (nextPosition: Point2D) => {
      const buildMetrics = buildMetricsRef.current;
      if (!buildMetrics) return;
      const currentScale = scene.viewport.scale.x;
      const { bounds } = buildMetrics.cameraBounds;

      const clampedPosition = (
        usesAdjustedViewport ? clampViewportDragPosition : clampLegacyViewportDragPosition
      )({
        position: nextPosition,
        scale: currentScale,
        bounds,
        screenWidth: viewportWidth,
        screenHeight: viewportHeight,
        isMobile,
      });

      applyCamera(scene, clampedPosition, currentScale, 'drag-move');
    };

    const clampDraggedPosition = () => {
      const buildMetrics = buildMetricsRef.current;
      if (!buildMetrics) return;
      const currentScale = scene.viewport.scale.x;
      const { bounds } = buildMetrics.cameraBounds;
      const position = { x: scene.viewport.x, y: scene.viewport.y };
      const clampedPosition = (
        usesAdjustedViewport ? clampViewportDragPosition : clampLegacyViewportDragPosition
      )({
        position,
        scale: currentScale,
        bounds,
        screenWidth: viewportWidth,
        screenHeight: viewportHeight,
        isMobile,
      });
      applyCamera(scene, clampedPosition, currentScale, 'drag-release-clamp');
    };

    const applyPinchPosition = (screenAnchor: Point2D, nextScale: number, worldAnchor: Point2D) => {
      const buildMetrics = buildMetricsRef.current;
      if (!buildMetrics) return;
      const unclampedPosition = buildViewportPositionForScreenAnchor(
        worldAnchor,
        screenAnchor,
        nextScale
      );
      const clampedPosition = (
        usesAdjustedViewport ? clampViewportPinchPosition : clampLegacyViewportPinchPosition
      )({
        position: unclampedPosition,
        scale: nextScale,
        bounds: buildMetrics.cameraBounds.bounds,
        screenWidth: viewportWidth,
        screenHeight: viewportHeight,
        isMobile,
      });

      applyCamera(scene, clampedPosition, nextScale, 'pinch-move');
    };

    const activateSectorAtClientPoint = (clientX: number, clientY: number) => {
      const sector = findSectorAtClientPoint(clientX, clientY);
      if (!sector) return false;

      const resolvedSectorName =
        normalizedSectorPathMap.get(normalizeMapSectorName(sector.name)) ?? sector.name;
      const isLodge =
        isLodgeSector(resolvedSectorName) || isRoyalVipFamilySector(resolvedSectorName);
      const hasSeats = lodgeSectorsWithSeats.has(normalizeMapSectorName(resolvedSectorName));
      const payload = isLodge ? lodgeSelectionPayloadBySectorName.get(resolvedSectorName) : null;

      if (isLodge && hasSeats) {
        if (payload) {
          lastSeatTapTsRef.current = Date.now();
          onSeatSelectRef.current(payload);
        }
        return true;
      }

      return false;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (isControlTarget(event.target)) return;

      const point = getHostPoint(event);
      activePointersRef.current.set(event.pointerId, point);

      if (activePointersRef.current.size === 1) {
        dragStateRef.current = {
          pointerId: event.pointerId,
          startPointer: point,
          startPosition: {
            x: scene.viewport.x,
            y: scene.viewport.y,
          },
        };
        pinchStateRef.current = null;
      } else if (activePointersRef.current.size === 2) {
        const [firstPoint, secondPoint] = [...activePointersRef.current.values()];
        const centerPoint = midpoint(firstPoint, secondPoint);
        const startDistance = distanceBetweenPoints(firstPoint, secondPoint);
        pendingSeatSelectionRef.current = null;
        pinchStateRef.current = {
          startDistance,
          startScale: scene.viewport.scale.x,
          anchorWorldPoint: scene.viewport.toWorld(centerPoint),
          needsAnchorReset: true,
        };
        dragStateRef.current = null;
        isDraggingRef.current = false;
      }

      if (!isMobile) {
        host.style.cursor = 'grab';
      }
      host.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!activePointersRef.current.has(event.pointerId)) return;

      const nextPoint = getHostPoint(event);
      activePointersRef.current.set(event.pointerId, nextPoint);

      if (activePointersRef.current.size === 2 && pinchStateRef.current) {
        const [firstPoint, secondPoint] = [...activePointersRef.current.values()];
        const centerPoint = midpoint(firstPoint, secondPoint);
        const currentDistance = distanceBetweenPoints(firstPoint, secondPoint);

        if (pinchStateRef.current.needsAnchorReset) {
          pinchStateRef.current = {
            startDistance: currentDistance,
            startScale: scene.viewport.scale.x,
            anchorWorldPoint: scene.viewport.toWorld(centerPoint),
            needsAnchorReset: false,
          };
          return;
        }

        const scaleFactor = currentDistance / pinchStateRef.current.startDistance;
        const nextScale = Math.max(
          minScaleRef.current,
          Math.min(2, pinchStateRef.current.startScale * scaleFactor)
        );

        applyPinchPosition(centerPoint, nextScale, pinchStateRef.current.anchorWorldPoint);
        return;
      }

      if (!dragStateRef.current || dragStateRef.current.pointerId !== event.pointerId) return;

      const deltaX = nextPoint.x - dragStateRef.current.startPointer.x;
      const deltaY = nextPoint.y - dragStateRef.current.startPointer.y;
      if (!isDraggingRef.current && Math.hypot(deltaX, deltaY) < 4) {
        return;
      }

      isDraggingRef.current = true;
      setSeatTooltip(null);
      setHoveredSectorName(null);
      if (!isMobile) {
        host.style.cursor = 'grabbing';
      }

      const nextPosition = {
        x: dragStateRef.current.startPosition.x + deltaX,
        y: dragStateRef.current.startPosition.y + deltaY,
      };
      applyDraggedPosition(nextPosition);
    };

    const releasePointer = (event: PointerEvent) => {
      const { pointerId } = event;
      const hadDragGesture = isDraggingRef.current;
      const releaseTs = Date.now();
      activePointersRef.current.delete(pointerId);
      const hadPinchGesture = activePointersRef.current.size < 2 && Boolean(pinchStateRef.current);

      if (activePointersRef.current.size < 2) {
        if (isMobile && pinchStateRef.current) {
          lastPinchEndTsRef.current = releaseTs;
        }
        pinchStateRef.current = null;
      }
      if (dragStateRef.current?.pointerId === pointerId) {
        dragStateRef.current = null;
      }
      if (activePointersRef.current.size === 0) {
        const shouldSuppressTap =
          isMobile && releaseTs - lastPinchEndTsRef.current < PINCH_TAP_SUPPRESSION_MS;
        if (hadDragGesture) {
          lastDragEndTsRef.current = releaseTs;
        }
        if (
          shouldSuppressTap &&
          (hadPinchGesture || pendingSeatSelectionRef.current?.pointerId === pointerId)
        ) {
        } else if (!hadDragGesture && pendingSeatSelectionRef.current?.pointerId === pointerId) {
          const pendingSelection = pendingSeatSelectionRef.current.seat;
          const isAlreadySelected = selectedSeats.some(
            selectedSeat => selectedSeat.seatId === pendingSelection.seatId
          );
          if (isAlreadySelected || selectedSeats.length < LIMIT_SEATS) {
            lastSeatTapTsRef.current = Date.now();
            onSeatSelectRef.current(pendingSelection);
            window.setTimeout(() => {
              redrawSeatSelectionState(pendingSelection.seatId, !isAlreadySelected);
            }, 0);
          }
        } else if (
          !shouldSuppressTap &&
          !hadDragGesture &&
          isMobile &&
          event.pointerType !== 'mouse'
        ) {
          const releasePoint = getHostPoint(event);
          const handledLodgeTap = activateSectorAtClientPoint(event.clientX, event.clientY);

          if (!handledLodgeTap) {
            const nearestSeatSelection = findNearestSeatSelection(releasePoint);

            if (nearestSeatSelection) {
              const isAlreadySelected = selectedSeats.some(
                selectedSeat => selectedSeat.seatId === nearestSeatSelection.seatId
              );
              if (isAlreadySelected || selectedSeats.length < LIMIT_SEATS) {
                lastSeatTapTsRef.current = Date.now();
                onSeatSelectRef.current(nearestSeatSelection);
                window.setTimeout(() => {
                  redrawSeatSelectionState(nearestSeatSelection.seatId, !isAlreadySelected);
                }, 0);
              }
            }
          }
        }
        pendingSeatSelectionRef.current = null;
        if (hadDragGesture) {
          clampDraggedPosition();
        }
        window.setTimeout(() => {
          isDraggingRef.current = false;
          if (!isMobile && host.isConnected) {
            host.style.cursor = 'grab';
          }
        }, 0);
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      releasePointer(event);
      if (host.hasPointerCapture(event.pointerId)) {
        host.releasePointerCapture(event.pointerId);
      }
    };

    const onPointerCancel = (event: PointerEvent) => {
      releasePointer(event);
      if (host.hasPointerCapture(event.pointerId)) {
        host.releasePointerCapture(event.pointerId);
      }
    };

    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerup', onPointerUp);
    host.addEventListener('pointercancel', onPointerCancel);

    return () => {
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerup', onPointerUp);
      host.removeEventListener('pointercancel', onPointerCancel);
      activePointersRef.current.clear();
      dragStateRef.current = null;
      pinchStateRef.current = null;
      pendingSeatSelectionRef.current = null;
      isDraggingRef.current = false;
      if (host.isConnected) {
        host.style.cursor = 'default';
      }
    };
  }, [
    isLodgeSector,
    isMobile,
    lodgeSelectionPayloadBySectorName,
    lodgeSectorsWithSeats,
    normalizeMapSectorName,
    normalizedSectorPathMap,
    scene,
    sectorPaths,
    selectedSeats,
    viewportHeight,
    viewportWidth,
    usesAdjustedViewport,
  ]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !scene) return;

    const isControlTarget = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest('[data-seatmap-control]'));

    const findSectorAtScreenPoint = (clientX: number, clientY: number) => {
      const screenPoint = new DOMPoint(clientX, clientY);

      for (const sector of sectorPaths) {
        const path = sectorHitPathRefs.current.get(sector.name);
        if (!path) continue;

        const screenMatrix = path.getScreenCTM();
        if (!screenMatrix) continue;

        const localPoint = screenPoint.matrixTransform(screenMatrix.inverse());
        const hitByFill = hasClosedSectorArea(sector.path, sector.type)
          ? path.isPointInFill(localPoint)
          : false;
        const hitByStroke =
          !hitByFill && !hasClosedSectorArea(sector.path, sector.type)
            ? path.isPointInStroke(localPoint)
            : false;

        if (hitByFill || hitByStroke) {
          return sector;
        }
      }

      return null;
    };

    const zoomToSector = (sectorName: string) => {
      const buildMetrics = buildMetricsRef.current;
      if (!buildMetrics) return;

      const sector = sectorPathByName.get(sectorName);
      if (!sector) return;

      const hitPath = sectorHitPathRefs.current.get(sector.name);
      const hostRect = host.getBoundingClientRect();
      const sectorRect = hitPath?.getBoundingClientRect();
      if (!sectorRect || sectorRect.width <= 0 || sectorRect.height <= 0) return;

      const topLeft = scene.viewport.toWorld({
        x: sectorRect.left - hostRect.left,
        y: sectorRect.top - hostRect.top,
      });
      const bottomRight = scene.viewport.toWorld({
        x: sectorRect.right - hostRect.left,
        y: sectorRect.bottom - hostRect.top,
      });
      const worldBounds = {
        minX: Math.min(topLeft.x, bottomRight.x),
        minY: Math.min(topLeft.y, bottomRight.y),
        maxX: Math.max(topLeft.x, bottomRight.x),
        maxY: Math.max(topLeft.y, bottomRight.y),
      };
      const worldCenter = {
        x: (worldBounds.minX + worldBounds.maxX) / 2,
        y: (worldBounds.minY + worldBounds.maxY) / 2,
      };
      const boundsWidth = Math.max(1, worldBounds.maxX - worldBounds.minX);
      const boundsHeight = Math.max(1, worldBounds.maxY - worldBounds.minY);
      const baseScale = Math.min(
        (viewportWidth * 0.8) / boundsWidth,
        (viewportHeight * 0.8) / boundsHeight,
        1
      );
      const zoomLevels = [baseScale, baseScale * 1.5, baseScale * 2.5];
      const currentZoomLevel = sectorZoomLevelsRef.current.get(sectorName) ?? 0;
      const nextZoomLevel = Math.min(currentZoomLevel + 1, zoomLevels.length - 1);
      const desiredScale = Math.min(2, Math.max(minScaleRef.current, zoomLevels[nextZoomLevel]));

      sectorZoomLevelsRef.current.set(sectorName, nextZoomLevel);
      const centeredPosition = buildCenteredViewportPosition(
        worldCenter,
        desiredScale,
        viewportWidth,
        viewportHeight
      );
      const clampedPosition = (
        usesAdjustedViewport ? clampViewportPosition : clampLegacyViewportPosition
      )({
        position: centeredPosition,
        scale: desiredScale,
        bounds: buildMetrics.cameraBounds.bounds,
        screenWidth: viewportWidth,
        screenHeight: viewportHeight,
        isMobile,
      });
      applyCamera(scene, clampedPosition, desiredScale, 'desktop-sector-click-zoom');
    };

    const onPointerMove = (event: PointerEvent) => {
      if (isMobile) return;
      if (isControlTarget(event.target)) return;
      if (seatHoverActiveRef.current || isDraggingRef.current) return;
      if (Date.now() - lastDragEndTsRef.current < 250) return;

      const sector = findSectorAtScreenPoint(event.clientX, event.clientY);

      if (sector) {
        const resolvedSectorName =
          normalizedSectorPathMap.get(normalizeMapSectorName(sector.name)) ?? sector.name;
        setHoveredSectorName(resolvedSectorName);
        host.style.cursor = 'pointer';
      } else if (!seatHoverActiveRef.current) {
        setHoveredSectorName(null);
        host.style.cursor = 'default';
      }
    };

    const onPointerLeave = () => {
      if (isMobile) return;
      if (!seatHoverActiveRef.current) {
        setHoveredSectorName(null);
        host.style.cursor = 'default';
      }
    };

    const onClick = (event: MouseEvent) => {
      if (isMobile) return;
      if (isControlTarget(event.target)) return;
      if (seatHoverActiveRef.current || isDraggingRef.current) return;
      if (Date.now() - lastDragEndTsRef.current < 250) return;
      if (Date.now() - lastSeatTapTsRef.current < 250) return;

      const sector = findSectorAtScreenPoint(event.clientX, event.clientY);
      if (!sector) return;

      const resolvedSectorName =
        normalizedSectorPathMap.get(normalizeMapSectorName(sector.name)) ?? sector.name;
      const isLodge =
        isLodgeSector(resolvedSectorName) || isRoyalVipFamilySector(resolvedSectorName);
      const hasSeats = lodgeSectorsWithSeats.has(normalizeMapSectorName(resolvedSectorName));
      if (isLodge && hasSeats) {
        const payload = lodgeSelectionPayloadBySectorName.get(resolvedSectorName);
        if (payload) {
          onSeatSelectRef.current(payload);
        }
        return;
      }
      zoomToSector(resolvedSectorName);
    };

    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerleave', onPointerLeave);
    host.addEventListener('click', onClick);

    return () => {
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerleave', onPointerLeave);
      host.removeEventListener('click', onClick);
    };
  }, [
    viewportHeight,
    viewportWidth,
    isLodgeSector,
    lodgeSelectionPayloadBySectorName,
    lodgeSectorsWithSeats,
    normalizeMapSectorName,
    normalizedSectorPathMap,
    scene,
    usesAdjustedViewport,
    sectorPathByName,
    sectorPaths,
  ]);

  const visibleSectorNames = useMemo(
    () =>
      buildVisibleSectorNames({
        hoveredSectorName,
        selectedLodgeSectorNames,
        sectorPaths,
        lodgeSectorsWithSeats,
        normalizeSectorName: normalizeMapSectorName,
      }),
    [
      hoveredSectorName,
      lodgeSectorsWithSeats,
      normalizeMapSectorName,
      sectorPaths,
      selectedLodgeSectorNames,
    ]
  );
  const visibleSectorEntries = useMemo(
    () =>
      [...visibleSectorNames]
        .map(name => sectorPathByName.get(name))
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)),
    [sectorPathByName, visibleSectorNames]
  );
  useEffect(() => {
    if (!scene) return;

    const { sectorLayer } = scene;
    const sectorChildren = sectorLayer.removeChildren();
    sectorChildren.forEach(child => child.destroy());

    outlineSectorPaths
      .filter(sector => isLodgeSector(sector.name) || isRoyalVipFamilySector(sector.name))
      .forEach(sector => {
        const isSelected = selectedLodgeSectorNames.has(sector.name);
        const isPriceHighlighted = priceHighlightedLodgeSectorNames.has(sector.name);
        const highlightColor = isPriceHighlighted ? LODGE_PRICE_HIGHLIGHT_COLOR : undefined;
        if (isPriceHighlighted) {
          sectorLayer.addChild(buildLodgePriceGlowGraphic({ sectorPath: sector.path }));
        }
        const fillGraphic = new Graphics();
        fillGraphic.svg(`
          <svg xmlns="http://www.w3.org/2000/svg">
            <path
              d="${sector.path}"
              fill="${isSelected ? '#4398da' : (highlightColor ?? LODGE_DEFAULT_FILL)}"
              fill-opacity="${isSelected ? 0.53 : isPriceHighlighted ? LODGE_PRICE_HIGHLIGHT_FILL_OPACITY : 1}"
              stroke="none"
            />
          </svg>
        `);
        sectorLayer.addChild(fillGraphic);
      });

    visibleSectorEntries.forEach(sector => {
      const isLodge = isLodgeSector(sector.name) || isRoyalVipFamilySector(sector.name);
      const hasSeats = lodgeSectorsWithSeats.has(normalizeMapSectorName(sector.name));
      const isSelected = hasSeats && selectedLodgeSectorNames.has(sector.name);
      const isHovered = hoveredSectorName === sector.name;
      const isPriceHighlighted = priceHighlightedLodgeSectorNames.has(sector.name);
      const stroke = isLodge
        ? !isSelected && isPriceHighlighted
          ? LODGE_PRICE_HIGHLIGHT_COLOR
          : hasSeats
            ? LODGE_HOVER_STROKE
            : ACTIVE_SECTOR_STROKE
        : ACTIVE_SECTOR_STROKE;
      const baseLodgeStroke = hasSeats ? LODGE_AVAILABLE_STROKE_WIDTH : 1.75;
      const strokeWidth = isLodge
        ? isSelected || isHovered || isPriceHighlighted
          ? baseLodgeStroke * 1.75
          : baseLodgeStroke
        : isHovered
          ? 2
          : 1;
      const pixiStrokeWidth = isLodge
        ? strokeWidth * PIXI_LODGE_STROKE_SCALE
        : strokeWidth * PIXI_SECTOR_STROKE_SCALE;
      const fill = 'none';

      const graphic = new Graphics();
      graphic.svg(`
        <svg xmlns="http://www.w3.org/2000/svg">
          <path
            d="${sector.path}"
            fill="${fill}"
            stroke="${stroke}"
            stroke-width="${pixiStrokeWidth}"
            stroke-linejoin="round"
            stroke-linecap="round"
            vector-effect="non-scaling-stroke"
          />
        </svg>
      `);
      sectorLayer.addChild(graphic);
    });

    outlineSectorPaths
      .filter(sector => isLodgeSector(sector.name))
      .forEach(sector => {
        const normalized = normalizeMapSectorName(sector.name);
        const center = sectorCenters.get(sector.name);
        const mappedLabel = lodgeLabelMap.get(normalized);
        const nearestLabel =
          !mappedLabel && center
            ? lodgeLabels.reduce<SectorLabelData | null>((closest, currentLabel) => {
                const dxCurrent = currentLabel.x - center.x;
                const dyCurrent = currentLabel.y - center.y;
                const currentDistance = dxCurrent * dxCurrent + dyCurrent * dyCurrent;

                if (!closest) return currentLabel;

                const dxClosest = closest.x - center.x;
                const dyClosest = closest.y - center.y;
                const closestDistance = dxClosest * dxClosest + dyClosest * dyClosest;
                return currentDistance < closestDistance ? currentLabel : closest;
              }, null)
            : null;
        const label = mappedLabel ?? nearestLabel;
        if (!center && !label) return;

        const fontSize = label?.fontSize ?? 72;
        const text = label?.text ?? sector.name;
        const x = center?.x ?? label?.x ?? 0;
        const y = center?.y ?? label?.y ?? 0;
        const isSelected = selectedLodgeSectorNames.has(sector.name);
        const textGraphic = new Text({
          text,
          style: {
            fill: isSelected ? '#1f1f1f' : '#4a4a4a',
            fontFamily: "'0_BlackerSansProVariableGX', 'Tahoma', sans-serif",
            fontSize,
            align: 'center',
          },
        });
        textGraphic.anchor.set(0.5);
        textGraphic.position.set(x, y);
        sectorLayer.addChild(textGraphic);
      });

    return () => {
      const cleanupChildren = sectorLayer.removeChildren();
      cleanupChildren.forEach(child => child.destroy());
    };
  }, [
    hoveredSectorName,
    isLodgeSector,
    lodgeLabelMap,
    lodgeLabels,
    lodgeSectorsWithSeats,
    normalizeMapSectorName,
    outlineSectorPaths,
    priceHighlightedLodgeSectorNames,
    scene,
    sectorCenters,
    selectedLodgeSectorNames,
    visibleSectorEntries,
  ]);

  return (
    <div
      className="flex max-w-full"
      style={{
        width: isMobile ? '100%' : stageWidth + DESKTOP_ZOOM_CONTROLS_WIDTH,
        height: stageHeight,
        borderTop: '1px solid var(--separator)',
      }}
    >
      <div
        ref={hostRef}
        data-seat-map-status={isSeatMapReady ? 'ready' : 'loading'}
        data-event-id={eventId}
        data-available-seats-count={isSeatMapReady ? renderedSeatMap.availableSeatsCount : 0}
        data-check-seat-id={isSeatMapReady ? renderedSeatMap.checkSeatId : undefined}
        data-check-seat-x={isSeatMapReady ? renderedSeatMap.checkSeatX : undefined}
        data-check-seat-y={isSeatMapReady ? renderedSeatMap.checkSeatY : undefined}
        style={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          height: '100%',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        <div
          ref={canvasMountRef}
          style={{
            position: 'absolute',
            inset: 0,
          }}
        />
        <SectorOverlay
          cameraSnapshot={cameraSnapshot}
          canvasWidth={viewportWidth}
          canvasHeight={viewportHeight}
          scene={scene}
          normalizedOffset={buildMetricsRef.current?.normalizedOffset ?? null}
          sectorPreviewTransform={sectorPreviewTransform}
          sectorPaths={outlineSectorPaths}
          sectorHitPathRefs={sectorHitPathRefs}
        />
        <SeatTooltip tooltip={seatTooltip} hidden={Boolean(isTimeoutModalOpen || isMobile)} />
        <SoldOutBanner
          visible={
            isSoldOut && isSeatMapReady && (parsedSeats.length > 0 || backgroundAsset !== null)
          }
        />
      </div>
      <ZoomControls
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        hidden={Boolean(isTimeoutModalOpen || isMobile)}
        onZoomIn={() => zoomByFactor(1.5)}
        onZoomOut={() => zoomByFactor(1 / 1.5)}
      />
    </div>
  );
};

export default SeatMapWebgl;
